import { logger } from "../../../utilities/logger";
import { OutlookUtils } from "../../../utilities/OutlookUtils";
import { ModeEnum } from "../components/enums/modeEnum";
import { DirectoryTreeType } from "../config/config";
import { IFolderLocation, ISharepointDocumentLocation } from "../interfaces/ISharepointDocument";
import { ISiteInfo, ISiteInfoLocation } from "../interfaces/ISiteInfo";
import { apiGetTessAccounts, apiGetTessProjects, apiPostTessEmail } from "./ApiService";
import { getItemCustomSelect, getSharePointSiteFromDriveId, getSiteByWebUrl, getSiteDriveByName, getSiteDrives } from "./GraphService";

export interface ITessUserIdentifier {
    tenantId: string;
    accountId: string;
}

export interface ITessTokenRequest extends ITessUserIdentifier {
    code: string;
}

export interface ITessAccountsResult {
    allowLoadAllAccounts: boolean;
    accounts: ITessAccount[];
}

export interface ITessAccount {
    phone: string;
    name: string;
    id: string;
    email: string;
    dossierMetadata: ITessDossier[];
    directMatch: boolean;
    contacts: any[];
    // Optional; Salesforce may omit it. Only an explicit false marks the account
    // inactive - see isTessAccountInactive.
    active?: boolean;
    accountNumber: string;
}

// THE rule for an "Inactief" Tess account, mirroring the client-list site flag: only an
// explicit `active: false` counts. Missing, null, or non-boolean values keep the account
// active so older Salesforce payloads render exactly as before.
export const isTessAccountInactive = (account: ITessAccount | null | undefined): boolean => account?.active === false;

export interface ITessProjectFolders {
    finished: ITessProject[];
    drafts: ITessProject[];
    active: ITessProject[];
    account: ITessAccount;
}

export interface ITessProject {
    year: number | null;
    status: string;
    serviceName: string;
    period: string | null;
    name: string;
    id: string;
    dossierMetadata: ITessDossier[] | null;
}

// One SharePoint target of a Tess account or project, as Salesforce sends it. Every field
// is optional at runtime (the API proxies the apex JSON untouched); which ones are present
// decides the shape - see resolveTessDossiers.
export interface ITessDossier {
    itemId?: string;
    driveId?: string;
    name?: string;
    siteUrl?: string;
    driveName?: string;
}

export enum TessProjectStatusEnum {
    Active = "active",
    Draft = "drafts",
    Finished = "finished",
}

export const TessProjectStatuses = [
    { key: TessProjectStatusEnum.Active },
    { key: TessProjectStatusEnum.Draft },
    { key: TessProjectStatusEnum.Finished }
];

export interface ITessProjectFolderLocation {
    id: string;
    name: string;
    treeType?: DirectoryTreeType;
    level?: number;
    isSelected?: boolean;
    canExpand?: boolean;
    expanded?: boolean;
    isLoading?: boolean;
    randomId: string;
    loaded?: boolean;
    projects: ITessProjectLocation[];
}

export interface ITessProjectLocation extends ITessProject {
    accountId: string;
    tessProjectId?: string;
    treeType?: DirectoryTreeType;
    level?: number;
    isSelected?: boolean;
    canExpand?: boolean;
    expanded?: boolean;
    isLoading?: boolean;
    randomId?: string;
    loaded?: boolean;
    documents?: ISharepointDocumentLocation[];
    folders?: IFolderLocation[];
}

export interface ITessAccountLocation extends ISiteInfoLocation {
    accountId: string;
    projectFolders?: ITessProjectFolderLocation[];
}

export interface ITessAccountSite extends ISiteInfo {
    accountId: string;
    allowToggle?: boolean;
}

// The one mapping from a Salesforce account to the site shape the location trees render.
// `allowToggle` marks sites the "show all accounts" toggle added, so switching it off can
// drop exactly those again; it is left off entirely for address-matched accounts.
export const toTessAccountSite = (account: ITessAccount, allowToggle?: boolean): ITessAccountSite => {
    const site: ITessAccountSite = {
        accountId: account.id,
        id: account.id,
        name: account.name,
        webUrl: "",
        isSubsite: false,
        isInactive: isTessAccountInactive(account)
    };
    if (allowToggle) {
        site.allowToggle = true;
    }
    return site;
};

export interface ITessEmailRequest {
    accountId: string; // Salesforce ID of the Account
    workId?: string; // Salesforce ID of the Work (Project) - if applicable
    messageIdentifier?: string; // Identifier of the email message - internetMessageId
    threadIdentifier?: string; // Identifier of the email thread
    fromName?: string; // From: Name of the sender
    fromEmail: string; // From: Email address of the sender
    toEmails: string[]; // From: Email address of the receiver
    ccEmails: string[]; // CC: List of email addresses of other recipients
    incoming: boolean; // true = Retrieved email message, false = Sent email message
    subject: string;
    htmlBody: string;
    messageDate: string; // Send/Receive date-time
    attachments: ITessAttachmentLink[]; // List of links to attachments
}

export interface ITessAttachmentLink {
    label: string; // The label of filename of the attachment
    url: string; // The web-url of the attachment that is archived
}

export interface ITessEmailTaskResponse {
    ok: boolean; // Created successfully true/false
    errorMessage?: string; // Message on error
}

export const getStatusText = (languageStrings: any, status: TessProjectStatusEnum): string => {
    if (status === TessProjectStatusEnum.Active) return languageStrings.TessActiveProjects;
    if (status === TessProjectStatusEnum.Draft) return languageStrings.TessConceptProjects;
    if (status === TessProjectStatusEnum.Finished) return languageStrings.TessCompletedProjects;
    return null;
}

export const getTessAccounts = async (accountEmailAddresses?: string[]): Promise<ITessAccountsResult> => {
    // https://dalemeare.my.salesforce.com/services/apexrest/tess/AddressBook/email?email=info@acuity.nl&dossier=true
    // https://dalemeare.my.salesforce.com/services/apexrest/tess/AddressBook/all?dossier=true
    return await apiGetTessAccounts(accountEmailAddresses);
}

export const getTessAccountProjects = async (accountId: string): Promise<ITessProjectFolders> => {
    // https://dalemeare.my.salesforce.com/services/apexrest/tess/Work?accountId=0015J000006FUv0QAG&dossier=true
    const projectResult: ITessProjectFolders = await apiGetTessProjects(accountId);
    return projectResult;
}

export const saveEmail = async (tessAccountId: string, tessProjectId: string, webUrl: string, mode: ModeEnum, mailItem: any, succeededFiles: any[]) => {
    try {
        if (mailItem && succeededFiles?.length > 0) {
            let origin: string = new URL(webUrl).origin;
            if (origin.endsWith("/")) {
                origin = origin.slice(0, -1);
            }

            const savedAttachments: ITessAttachmentLink[] = succeededFiles
                .filter(f => f.isAttachment)
                .map(f => ({ label: f.name, url: origin + f.serverRelativeUrl }));

            let requestPayload: ITessEmailRequest;
            if (mode !== ModeEnum.mailCompose) {
                requestPayload = {
                    accountId: tessAccountId,
                    workId: tessProjectId,
                    messageIdentifier: mailItem.internetMessageId,
                    threadIdentifier: mailItem.conversationId,
                    fromName: mailItem.from?.displayName ?? mailItem.from?.emailAddress,
                    fromEmail: mailItem.from?.emailAddress ?? "",
                    toEmails: mailItem.to?.map(t => t.emailAddress) ?? [],
                    ccEmails: mailItem.cc?.map(cc => cc.emailAddress) ?? [],
                    incoming: mailItem.from?.emailAddress !== Office.context?.mailbox?.userProfile?.emailAddress,
                    subject: mailItem.subject,
                    htmlBody: await OutlookUtils.getMailItemHTMLBody(mailItem),
                    messageDate: mailItem?.dateTimeCreated?.toISOString(),
                    attachments: savedAttachments
                };
            }
            else {
                let mailboxAddress: string | undefined;
                if (Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
                    mailboxAddress = await OutlookUtils.getMailboxAddress()
                }

                requestPayload = {
                    accountId: tessAccountId,
                    workId: tessProjectId,
                    messageIdentifier: mailItem.internetMessageId,
                    threadIdentifier: mailItem.conversationId,
                    fromName: mailboxAddress ?? Office.context?.mailbox?.userProfile?.displayName ?? Office.context?.mailbox?.userProfile?.emailAddress,
                    fromEmail: mailboxAddress ?? Office.context?.mailbox?.userProfile?.emailAddress ?? "",
                    toEmails: mailItem.toRecipients?.map(t => t.emailAddress?.address) ?? [],
                    ccEmails: mailItem.ccRecipients?.map(cc => cc.emailAddress?.address) ?? [],
                    incoming: false,
                    subject: mailItem.subject,
                    htmlBody: mailItem.body?.content ?? "",
                    messageDate: mailItem.sentDateTime ?? new Date().toISOString(),
                    attachments: savedAttachments
                };
            }

            await apiPostTessEmail(requestPayload);
        }
    } catch (error) {
        logger(error, "OutlookUtils.ts (saveEmail)");
    }
}

// $select sets shared by every consumer of resolveTessDossiers: the union of what the
// content manager and the taskpane trees read off a resolved drive / folder. Drives cannot
// $select driveItem-only properties (webDavUrl, folder), hence two sets. Exported so the
// tests can pin select parity.
export const TESS_LIBRARY_SELECT = "id,name,description,createdDateTime,lastModifiedDateTime,webUrl,sharepointIds,parentReference";
export const TESS_FOLDER_SELECT = "id,name,description,createdDateTime,lastModifiedDateTime,webUrl,webDavUrl,folder,sharepointIds";

// The slice of a Graph drive the consumers read. Graph spells the facet `sharePointIds` on
// drives but `sharepointIds` on drive items; the two types below keep that straight at
// every consumer.
export interface ITessResolvedDrive {
    id: string;
    name?: string;
    description?: string;
    createdDateTime?: string;
    lastModifiedDateTime?: string;
    webUrl?: string;
    sharePointIds: { listId: string; siteId?: string; siteUrl?: string; };
    parentReference?: { siteId?: string; };
}

export interface ITessResolvedItem {
    id: string;
    name?: string;
    description?: string;
    createdDateTime?: string;
    lastModifiedDateTime?: string;
    webUrl?: string;
    webDavUrl?: string;
    folder?: { childCount?: number; };
    sharepointIds: { listId: string; siteId?: string; siteUrl?: string; listItemUniqueId?: string; };
}

// A dossier resolved to one SharePoint library. `displayName` is what the row shows:
// the dossier's own name for the single-drive shapes, the drive's name when one
// siteUrl-only dossier fans out into every library on its site.
export interface ITessResolvedLibrary {
    dossier: ITessDossier;
    displayName: string;
    drive: ITessResolvedDrive;
}

// A dossier resolved to one folder inside a library.
export interface ITessResolvedFolder {
    dossier: ITessDossier;
    item: ITessResolvedItem;
}

export interface ITessResolvedDossiers {
    libraries: ITessResolvedLibrary[];
    folders: ITessResolvedFolder[];
}

// Salesforce may send "" as readily as omit a field; both mean "not set".
const isBlank = (value: string | null | undefined): boolean => typeof value !== "string" || value.trim().length === 0;

const describeDossier = (dossier: ITessDossier | null | undefined): string => isBlank(dossier?.name) ? "(unnamed)" : `"${dossier.name}"`;

interface ITessResolvedEntry extends ITessResolvedDossiers {
    // True when the libraries came from a siteUrl-only fan-out rather than an explicit target.
    fromSiteFanOut: boolean;
    // Why nothing could be resolved, for the summary log.
    problem?: string;
}

const resolveOneDossier = async (accessToken: string, dossier: ITessDossier | null | undefined): Promise<ITessResolvedEntry> => {
    const none: ITessResolvedEntry = { libraries: [], folders: [], fromSiteFanOut: false };
    if (!dossier) {
        return { ...none, problem: "(empty entry)" };
    }

    const hasDriveId = !isBlank(dossier.driveId);
    const hasItemId = !isBlank(dossier.itemId);
    const hasSiteUrl = !isBlank(dossier.siteUrl);
    const hasDriveName = !isBlank(dossier.driveName);

    if (hasItemId && !hasDriveId) {
        // A folder id is only addressable through its drive.
        return { ...none, problem: `${describeDossier(dossier)}: itemId without driveId` };
    }
    if (!hasDriveId && !hasSiteUrl) {
        return { ...none, problem: `${describeDossier(dossier)}: no driveId or siteUrl` };
    }

    // The Graph helpers swallow their own errors today; the catch keeps one dossier's
    // failure from discarding every other dossier should that ever change.
    try {
        if (hasDriveId && hasItemId) {
            const item: ITessResolvedItem = await getItemCustomSelect(accessToken, dossier.driveId, dossier.itemId, TESS_FOLDER_SELECT);
            return item ? { ...none, folders: [{ dossier, item }] } : none;
        }

        if (hasDriveId) {
            const drive: ITessResolvedDrive = await getItemCustomSelect(accessToken, dossier.driveId, null, TESS_LIBRARY_SELECT);
            return drive ? { ...none, libraries: [{ dossier, displayName: dossier.name, drive }] } : none;
        }

        if (hasDriveName) {
            const drive: ITessResolvedDrive = await getSiteDriveByName(dossier.siteUrl, dossier.driveName);
            return drive ? { ...none, libraries: [{ dossier, displayName: dossier.name, drive }] } : none;
        }

        const drives: ITessResolvedDrive[] = await getSiteDrives(dossier.siteUrl);
        return {
            ...none,
            fromSiteFanOut: true,
            libraries: drives.map(drive => ({ dossier, displayName: isBlank(drive.name) ? dossier.name : drive.name, drive }))
        };
    } catch (error) {
        return { ...none, problem: `${describeDossier(dossier)}: lookup failed: ${error?.message ?? error}` };
    }
};

// THE classifier for Tess dossier metadata. Salesforce hands over one of four shapes (the
// API proxies the apex JSON untouched, so any field may be missing or empty):
//   driveId + itemId     -> that folder
//   driveId              -> that library
//   siteUrl + driveName  -> the one drive on that site with that name (no fallback on a miss)
//   siteUrl              -> every document library on that site, one row per drive
// Anything else cannot be resolved and is logged (once per call) rather than silently
// dropped. Output keeps dossier order; libraries and folders come back in separate lists
// because every consumer renders them as different location types.
//
// A library an explicit dossier targets wins over a siteUrl fan-out that also lists it, and
// a library listed by several fan-outs appears once. Explicit dossiers are never deduped
// against each other: that is pre-existing behaviour and may be deliberate data.
export const resolveTessDossiers = async (accessToken: string, dossierMetadata: ITessDossier[] | null | undefined): Promise<ITessResolvedDossiers> => {
    const dossiers: ITessDossier[] = Array.isArray(dossierMetadata) ? dossierMetadata : [];
    if (dossiers.length === 0) {
        return { libraries: [], folders: [] };
    }

    const entries: ITessResolvedEntry[] = await Promise.all(dossiers.map(dossier => resolveOneDossier(accessToken, dossier)));

    const problems: string[] = entries.filter(entry => entry.problem).map(entry => entry.problem);
    if (problems.length > 0) {
        // One client-error row per resolve, not one per dossier: the same account is
        // re-resolved on every tree expand and every content-manager load.
        logger(new Error(`${problems.length} of ${dossiers.length} Tess dossier(s) could not be resolved: ${problems.join("; ")}`), "TessService.ts (resolveTessDossiers)");
    }

    const explicitDriveIds = new Set<string>();
    entries.forEach(entry => {
        if (!entry.fromSiteFanOut) {
            entry.libraries.forEach(library => explicitDriveIds.add(library.drive.id));
        }
    });

    const libraries: ITessResolvedLibrary[] = [];
    const folders: ITessResolvedFolder[] = [];
    const seenFanOutDriveIds = new Set<string>();
    entries.forEach(entry => {
        folders.push(...entry.folders);
        if (!entry.fromSiteFanOut) {
            libraries.push(...entry.libraries);
            return;
        }
        entry.libraries.forEach(library => {
            const driveId = library.drive.id;
            if (explicitDriveIds.has(driveId) || seenFanOutDriveIds.has(driveId)) {
                return;
            }
            seenFanOutDriveIds.add(driveId);
            libraries.push(library);
        });
    });

    return { libraries, folders };
};

// True for the whole-site dossier shape: a site url and nothing narrower.
const isSiteOnlyDossier = (dossier: ITessDossier | null | undefined): boolean =>
    !!dossier && !isBlank(dossier.siteUrl) && isBlank(dossier.driveId) && isBlank(dossier.driveName) && isBlank(dossier.itemId);

const lookupSiteByUrl = async (siteUrl: string): Promise<{ id: string; webUrl: string; displayName: string; } | null> => {
    const site = await getSiteByWebUrl(siteUrl);
    return site ? { id: site.id, webUrl: site.webUrl, displayName: site.displayName } : null;
};

// The SharePoint site a Tess project lives on, for "open in browser" and pinning.
//
// A site-only dossier is the project's home: the first one that resolves wins, in dossier
// order. Only when there is none (or none resolves) does the lookup fall back to walking
// every dossier in order - a dossier's driveId before its siteUrl - which is exactly what
// the old `dossierMetadata[0].driveId` lookup did for the first dossier, so projects without
// a site-only dossier open the site they always did. Failed lookups are logged and skipped.
export const getTessProjectSite = async (dossierMetadata: ITessDossier[] | null | undefined): Promise<{ id: string; webUrl: string; displayName: string; } | null> => {
    const dossiers: ITessDossier[] = (Array.isArray(dossierMetadata) ? dossierMetadata : []).filter(dossier => !!dossier);
    const source = "TessService.ts (getTessProjectSite)";

    const siteOnlyDossiers = dossiers.filter(isSiteOnlyDossier);
    for (const dossier of siteOnlyDossiers) {
        try {
            const site = await lookupSiteByUrl(dossier.siteUrl);
            if (site) {
                return site;
            }
        } catch (error) {
            logger(error, source);
        }
    }

    for (const dossier of dossiers) {
        if (isSiteOnlyDossier(dossier)) {
            continue; // already tried above
        }
        try {
            if (!isBlank(dossier.driveId)) {
                const site = await getSharePointSiteFromDriveId(dossier.driveId);
                if (site) {
                    return site;
                }
            }
            if (!isBlank(dossier.siteUrl)) {
                const site = await lookupSiteByUrl(dossier.siteUrl);
                if (site) {
                    return site;
                }
            }
        } catch (error) {
            logger(error, source);
        }
    }

    return null;
};
